import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SOLUCOES_LABELS } from '@/pages/painel/lib/radarConstants';
import {
  type OportunidadeConsolidada,
  type ComercialKpis,
  FAIXA_LABELS,
  FAIXA_TONES,
  FAIXA_ICONS,
  MARGEM_TONES,
  formatBRL,
  formatPercent,
} from '@/pages/painel/lib/comercial';
import OportunidadeDetalheModal from './OportunidadeDetalheModal';

const ESTADO_OPTIONS = [
  { key: 'todas', label: 'Todas' },
  { key: 'abertas', label: 'Abertas' },
  { key: 'ganhas', label: 'Ganhas' },
  { key: 'perdidas', label: 'Perdidas' },
];

function formatValor(v: number | null | undefined): string {
  return v == null || v === 0 ? 'Não calculável' : formatBRL(v);
}

export default function OportunidadesModule() {
  const { password } = useOutletContext<{ password: string }>();

  const [ops, setOps] = useState<OportunidadeConsolidada[]>([]);
  const [kpis, setKpis] = useState<ComercialKpis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<OportunidadeConsolidada | null>(null);

  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('todas');
  const [faixa, setFaixa] = useState('todas');
  const [origem, setOrigem] = useState('todas');
  const [cidade, setCidade] = useState('');
  const [solucao, setSolucao] = useState('');
  const [margem, setMargem] = useState('');
  const [prob, setProb] = useState('');
  const [semAcao, setSemAcao] = useState(false);
  const [fecha30, setFecha30] = useState(false);

  const load = async (withFilters?: Record<string, any>) => {
    setLoading(true);
    setError(null);
    try {
      const filtros: Record<string, any> = {
        ...(estado !== 'todas' ? { estado } : {}),
        ...(faixa !== 'todas' ? { faixa } : {}),
        ...(origem !== 'todas' ? { origem } : {}),
        ...(cidade ? { cidade } : {}),
        ...(solucao ? { solucao } : {}),
        ...(margem ? { margem } : {}),
        ...(prob ? { probabilidade: prob } : {}),
        ...(semAcao ? { sem_proxima_acao: true } : {}),
        ...(fecha30 ? { fechamento_30: true } : {}),
        search: search,
        ...(withFilters ?? {}),
      };
      const [listRes, resumoRes] = await Promise.all([
        supabase.functions.invoke('comercial-admin', { body: { action: 'listar', password, filtros } }),
        supabase.functions.invoke('comercial-admin', { body: { action: 'resumo', password } }),
      ]);
      if (listRes.error) throw new Error(await extractErrorMessage(listRes.error));
      if (resumoRes.error) throw new Error(await extractErrorMessage(resumoRes.error));
      const lista = listRes.data as { oportunidades?: OportunidadeConsolidada[]; error?: string };
      if (lista?.error) throw new Error(lista.error);
      const resumo = resumoRes.data as { kpis?: ComercialKpis; error?: string };
      if (resumo?.error) throw new Error(resumo.error);
      setOps(lista?.oportunidades ?? []);
      setKpis(resumo?.kpis ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar oportunidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const aplicarFiltros = () => load();

  const limparFiltros = () => {
    setSearch('');
    setEstado('todas');
    setFaixa('todas');
    setOrigem('todas');
    setCidade('');
    setSolucao('');
    setMargem('');
    setProb('');
    setSemAcao(false);
    setFecha30(false);
    setTimeout(() => load(), 0);
  };

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { label: 'Pipeline total', value: formatValor(kpis.pipeline), icon: 'ri-funds-line', tone: 'text-gray-900' },
      { label: 'Pipeline ponderado', value: formatValor(kpis.pipeline_ponderado), icon: 'ri-scales-3-line', tone: 'text-gray-900' },
      { label: 'Lucro potencial', value: formatValor(kpis.lucro_potencial), icon: 'ri-money-dollar-circle-line', tone: 'text-green-600' },
      { label: 'Lucro ponderado', value: formatValor(kpis.lucro_ponderado), icon: 'ri-coins-line', tone: 'text-green-600' },
      { label: 'Oportunidades', value: String(kpis.quantidade), icon: 'ri-rocket-2-line', tone: 'text-[#E8610A]' },
      { label: 'Ticket médio', value: kpis.ticket_medio ? formatBRL(kpis.ticket_medio) : 'Não calculável', icon: 'ri-price-tag-3-line', tone: 'text-gray-900' },
      { label: 'Margem média', value: formatPercent(kpis.margem_media), icon: 'ri-percent-line', tone: 'text-gray-900' },
      { label: 'Sem próxima ação', value: String(kpis.sem_proxima_acao), icon: 'ri-alert-line', tone: 'text-amber-600' },
      { label: 'Propostas sem resposta', value: String(kpis.propostas_sem_resposta), icon: 'ri-file-list-3-line', tone: 'text-red-500' },
      { label: 'Previsto 30 dias', value: formatValor(kpis.previsao_30), icon: 'ri-calendar-line', tone: 'text-gray-900' },
      { label: 'Previsto 60 dias', value: formatValor(kpis.previsao_60), icon: 'ri-calendar-2-line', tone: 'text-gray-900' },
      { label: 'Previsto 90 dias', value: formatValor(kpis.previsao_90), icon: 'ri-calendar-check-line', tone: 'text-gray-900' },
    ];
  }, [kpis]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-rocket-2-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Oportunidades</h2>
            <p className="text-sm text-gray-500">
              Visão comercial consolidada — CRM + Radar, com foco em lucro e previsão.
            </p>
          </div>
        </div>
        <button onClick={() => load()} disabled={loading} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60">
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-3.5">
            <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
              <i className={c.icon}></i> {c.label}
            </p>
            <p className={`text-base font-bold truncate ${c.tone}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por título, empresa, cidade..." className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
          </div>
          <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1 h-fit">
            {ESTADO_OPTIONS.map((s) => (
              <button key={s.key} onClick={() => setEstado(s.key)} className={`inline-flex items-center text-xs font-semibold rounded-full px-3 py-1.5 cursor-pointer whitespace-nowrap ${estado === s.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <select value={faixa} onChange={(e) => setFaixa(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
            <option value="todas">Todas as faixas</option>
            {Object.entries(FAIXA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={origem} onChange={(e) => setOrigem(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
            <option value="todas">Origem: todas</option>
            <option value="crm">CRM</option>
            <option value="radar">Radar</option>
          </select>
          <select value={solucao} onChange={(e) => setSolucao(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
            <option value="">Todas as soluções</option>
            {Object.entries(SOLUCOES_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={margem} onChange={(e) => setMargem(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
            <option value="">Toda margem</option>
            <option value="verde">Margem ok</option>
            <option value="amarela">Margem apertada</option>
            <option value="vermelha">Margem baixa</option>
          </select>
          <select value={prob} onChange={(e) => setProb(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
            <option value="">Toda probabilidade</option>
            <option value="alta">Alta (≥70%)</option>
            <option value="media">Média (40–69%)</option>
            <option value="baixa">Baixa (&lt;40%)</option>
          </select>
          <input type="text" value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Cidade" className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 w-32 focus:outline-none" />
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={semAcao} onChange={(e) => setSemAcao(e.target.checked)} className="cursor-pointer" />
            Sem próxima ação
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={fecha30} onChange={(e) => setFecha30(e.target.checked)} className="cursor-pointer" />
            Fecha em 30 dias
          </label>
          <button onClick={aplicarFiltros} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap">
            <i className="ri-filter-3-line"></i> Aplicar
          </button>
          <button onClick={limparFiltros} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap">
            Limpar
          </button>
        </div>
      </div>

      {/* Lista */}
      {loading && ops.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : ops.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-rocket-2-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500">Nenhuma oportunidade encontrada.</p>
          <p className="text-xs text-gray-400">Oportunidades do CRM e do Radar aparecem consolidadas aqui.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {ops.map((o) => {
            const margemTone = o.margem_tone ? MARGEM_TONES[o.margem_tone] : null;
            return (
              <button
                key={o.id}
                onClick={() => setSelected(o)}
                className="text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#E8610A]/40 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 leading-snug line-clamp-2">{o.titulo}</h3>
                    {o.empresa && <p className="text-xs text-gray-500 truncate mt-0.5">{o.empresa}</p>}
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border shrink-0 ${FAIXA_TONES[o.faixa]}`}>
                    <i className={FAIXA_ICONS[o.faixa]}></i>
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{o.origem === 'crm' ? 'CRM' : 'Radar'}</span>
                  {(o.cidade || o.uf) && <span className="text-xs text-gray-500">{[o.cidade, o.uf].filter(Boolean).join(' - ')}</span>}
                  {margemTone && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${margemTone.tone}`}>
                      <i className={margemTone.icon}></i> {formatPercent(o.margem)}
                    </span>
                  )}
                  {o.projeto_estrategico_id && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                      <i className="ri-focus-3-line"></i> Projeto
                    </span>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Valor</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{formatBRL(o.valor)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Lucro</p>
                    <p className="text-sm font-bold text-green-700 truncate">{formatBRL(o.lucro)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Prob.</p>
                    <p className="text-sm font-bold text-gray-900">{formatPercent(o.probabilidade)}</p>
                  </div>
                </div>

                {!o.proxima_acao && o.aberta && (
                  <p className="mt-3 text-[11px] font-semibold text-red-600 inline-flex items-center gap-1">
                    <i className="ri-alert-line"></i> Sem próxima ação
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <OportunidadeDetalheModal
          oportunidade={selected}
          password={password}
          onClose={() => setSelected(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}